/**
 * FocusTableStep - Step 1: Select primary table and basic info
 */

import React, { useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Select,
  Option,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Checkbox,
  Tooltip,
} from "@mui/joy";
import TableChartIcon from "@mui/icons-material/TableChart";
import SettingsIcon from "@mui/icons-material/Settings";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PublicIcon from "@mui/icons-material/Public";
import { useViewBuilderContext } from "../ViewBuilderContext";
import { useSchemas, useSchemaColumns } from "../../../hooks/useSchemas";
import type { SchemaTableWithColumns } from "../../../types";

interface FocusTableStepProps {
  workspaceId: string;
}

export default function FocusTableStep({ workspaceId }: FocusTableStepProps) {
  const {
    state,
    setName,
    setDescription,
    setIsTemplate,
    setIsPublic,
    setFocusTable,
    clearFocusTable,
    updateFocusTableColumns,
    getFieldError,
    nextStep,
  } = useViewBuilderContext();

  // Get validation errors for this step
  const nameError = getFieldError("name");
  const focusTableError = getFieldError("focusTable");

  const {
    schemas,
    loading: schemasLoading,
    error: schemasError,
  } = useSchemas(workspaceId);

  // Fetch columns for the selected focus table
  const { columns, loading: columnsLoading } = useSchemaColumns(
    workspaceId,
    state.focusTableId,
  );

  // Update focus table with columns when they're loaded
  // Use updateFocusTableColumns to avoid clearing dependent data (joins, fields, filters)
  useEffect(() => {
    if (state.focusTableId && columns.length > 0 && state.focusTable) {
      // Only update if columns have changed
      const currentColumnIds = (state.focusTable.columns || [])
        .map((c) => c.id)
        .sort()
        .join(",");
      const newColumnIds = columns
        .map((c) => c.id)
        .sort()
        .join(",");
      if (currentColumnIds !== newColumnIds) {
        updateFocusTableColumns(columns);
      }
    }
  }, [state.focusTableId, columns, state.focusTable, updateFocusTableColumns]);

  const handleTableChange = (
    _event: React.SyntheticEvent | null,
    value: string | null,
  ) => {
    if (!value) {
      clearFocusTable();
      return;
    }

    const selectedTable = schemas.find(
      (s: SchemaTableWithColumns) => s.id === value,
    );
    if (selectedTable) {
      // Set table initially without columns - columns will be fetched by useSchemaColumns
      setFocusTable(value, { ...selectedTable, columns: [] });

      // Auto-advance to next step if name is already filled
      if (state.name.trim()) {
        // Small delay for visual feedback before advancing
        setTimeout(() => nextStep(), 300);
      }
    }
  };

  return (
    <Stack spacing={3}>
      {/* Basic Information */}
      <Card variant="outlined">
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Basic Information
          </Typography>

          <Stack spacing={2}>
            <FormControl required error={!!nameError}>
              <FormLabel>View Name</FormLabel>
              <Input
                value={state.name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name for this view"
                color={nameError ? "danger" : undefined}
              />
              {nameError && <FormHelperText>{nameError}</FormHelperText>}
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={state.description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose of this view"
                minRows={2}
              />
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Focus Table Selection */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <TableChartIcon color="primary" />
            <Typography level="title-lg">Focus Table</Typography>
          </Box>

          <Typography level="body-sm" sx={{ color: "text.secondary", mb: 2 }}>
            Select the primary table for this view. This is the main data
            source, and all joins will be relative to this table.
          </Typography>

          {schemasError && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {schemasError}
            </Alert>
          )}

          <FormControl required error={!!focusTableError}>
            <FormLabel>Select Table</FormLabel>
            <Select
              value={state.focusTableId || ""}
              onChange={handleTableChange}
              placeholder={
                schemasLoading ? "Loading tables..." : "Select a table"
              }
              disabled={schemasLoading}
              color={focusTableError ? "danger" : undefined}
            >
              {schemas.map((table: SchemaTableWithColumns) => (
                <Option key={table.id} value={table.id}>
                  {table.display_name || table.table_name}
                </Option>
              ))}
            </Select>
            {focusTableError && (
              <FormHelperText>{focusTableError}</FormHelperText>
            )}
          </FormControl>

          {/* Selected Table Info */}
          {state.focusTable && (
            <Card variant="soft" sx={{ mt: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TableChartIcon sx={{ color: "primary.500" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography level="title-sm">
                      {state.focusTable.display_name ||
                        state.focusTable.table_name}
                    </Typography>
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.secondary" }}
                    >
                      {state.focusTable.table_name}
                    </Typography>
                  </Box>
                  <Chip size="sm" variant="soft" color="primary">
                    {columnsLoading ? (
                      <CircularProgress size="sm" sx={{ mr: 0.5 }} />
                    ) : null}
                    {state.focusTable.columns?.length || 0} columns
                  </Chip>
                </Stack>

                {state.focusTable.description && (
                  <Typography
                    level="body-sm"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    {state.focusTable.description}
                  </Typography>
                )}

                {/* Column Preview */}
                {state.focusTable.columns &&
                  state.focusTable.columns.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary", mb: 1 }}
                      >
                        Columns:
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {state.focusTable.columns.slice(0, 8).map((col) => (
                          <Chip
                            key={col.id}
                            size="sm"
                            variant="outlined"
                            color={col.is_primary_key ? "warning" : "neutral"}
                          >
                            {col.display_name || col.column_name}
                            {col.is_primary_key && " (PK)"}
                          </Chip>
                        ))}
                        {state.focusTable.columns.length > 8 && (
                          <Chip size="sm" variant="soft" color="neutral">
                            +{state.focusTable.columns.length - 8} more
                          </Chip>
                        )}
                      </Stack>
                    </Box>
                  )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* View Options */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <SettingsIcon color="primary" />
            <Typography level="title-lg">View Options</Typography>
          </Box>

          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Tooltip title="Templates can be cloned by other users to create their own views">
                <span>
                  <Checkbox
                    checked={state.isTemplate}
                    onChange={(e) => setIsTemplate(e.target.checked)}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ContentCopyIcon
                          sx={{ fontSize: 18, color: "neutral.500" }}
                        />
                        <Box>
                          <Typography level="body-sm" fontWeight="md">
                            Save as Template
                          </Typography>
                          <Typography
                            level="body-xs"
                            sx={{ color: "text.tertiary" }}
                          >
                            Allow others to clone this view
                          </Typography>
                        </Box>
                      </Stack>
                    }
                    sx={{ alignItems: "flex-start" }}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Tooltip title="Public views are visible to all workspace members">
                <span>
                  <Checkbox
                    checked={state.isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PublicIcon
                          sx={{ fontSize: 18, color: "neutral.500" }}
                        />
                        <Box>
                          <Typography level="body-sm" fontWeight="md">
                            Make Public
                          </Typography>
                          <Typography
                            level="body-xs"
                            sx={{ color: "text.tertiary" }}
                          >
                            All workspace members can see this view
                          </Typography>
                        </Box>
                      </Stack>
                    }
                    sx={{ alignItems: "flex-start" }}
                  />
                </span>
              </Tooltip>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
